import React from 'react';
import { Loader2, Send, RefreshCw } from 'lucide-react';
import { HiOutlineExternalLink } from 'react-icons/hi';
import { StepActions, StepActionButton, StepSkipButton } from '../ApplicationStepComponents';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectSessionAdvisor,
  selectClient,
  selectPolicyApplicationsStatus,
  selectSession,
} from '@client/model/selectors';
import {
  postPolicyApplicationEmailRequest,
  resetPolicyApplicationStateStatus,
} from '@client/model/policyApplicationModel';
import { useEffect, useMemo } from 'react';
import { useToast } from '@client/components/hooks/use-toast';
import { ApplicationConsentDialog } from '../ApplicationConsentDialog';
import { useAdvisorBGA } from '@client/hooks/useAdvisorBGA';
import { SessionType } from '@shared';

export type SubmissionMethod = 'submit-to-carrier' | 'submit-to-bga-imo' | null;
interface SubmitPolicyApplicationFormStepProps {
  clientPolicyApplicationId: string;
  policyFormMetadata: PolicyFormMetadata;
  formType: 'application' | 'pre-underwriting';
  policyFundingSource: PolicyFundingSource | undefined;
  onStepSelect?: React.Dispatch<React.SetStateAction<string | null>>;
  currentStep?: PolicyApplicationStep;
  stepStatus?: PolicyApplicationStep['status'] | undefined;
  onSkipForm?: () => void;
  selectedSubmissionMethod?: SubmissionMethod;
  onSubmissionMethodChange?: (method: SubmissionMethod) => void;
}

export function SubmitPolicyApplicationFormStep({
  clientPolicyApplicationId,
  policyFormMetadata,
  formType,
  policyFundingSource,
  onStepSelect,
  stepStatus,
  onSkipForm,
  selectedSubmissionMethod = null,
  onSubmissionMethodChange,
}: SubmitPolicyApplicationFormStepProps) {
  const {
    associatedBGA,
    hasBGAForCarrier,
    needsBGAConfiguration,
    showRefreshMessage,
    setShowRefreshMessage,
    getCarrierName,
  } = useAdvisorBGA({ policyType: policyFundingSource?.policyType || undefined });
  const dispatch = useDispatch();
  const session = useSelector(selectSession);
  const advisor = useSelector(selectSessionAdvisor);
  const client = useSelector(selectClient);
  const policyApplicationStatus = useSelector(selectPolicyApplicationsStatus);
  const { toast } = useToast();
  const isSkipping = policyApplicationStatus === 'savingStepStatus';

  // Generate copy based on form type
  const copy =
    formType === 'application'
      ? {
          title: 'Submit Policy Application Form',
          description:
            'Your policy application form is complete and ready to be submitted for review',
          buttonText: 'Submit Application',
          sendingText: 'Sending...',
          successToastTitle: 'Application Form Submitted',
          successToastDescription: 'Application form has been successfully submitted.',
          errorToastTitle: 'Error Submitting Application Form',
          errorToastDescription: 'Failed to submit application form.',
        }
      : {
          title: 'Submit Pre-Underwriting Form',
          description:
            'Your pre-underwriting form is complete and ready to be submitted for review',
          buttonText: 'Submit Pre-Underwriting Form',
          sendingText: 'Sending...',
          successToastTitle: 'Pre-Underwriting Form Submitted',
          successToastDescription: 'Pre-underwriting form has been successfully submitted.',
          errorToastTitle: 'Error Submitting Pre-Underwriting Form',
          errorToastDescription: 'Failed to submit pre-underwriting form.',
          skipTitle: 'Skip',
        };

  useEffect(() => {
    if (stepStatus === 'completed') {
      onStepSelect?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepStatus]);

  const isSendingEmail = policyApplicationStatus === 'sendingFormEmail';
  
  const currentUser = useMemo(() => {
    if (!session) return null;
    
    const isClient = (session.sessionType === SessionType.client);
    
    if (isClient) {
      return {
        type: 'client' as const,
      };
    }
    
    return {
      type: 'advisor' as const,
    };
  }, [session]);
  
  const isAdvisor = currentUser?.type === 'advisor';

  useEffect(() => {
    if (policyApplicationStatus === 'sendingFormEmailComplete') {
      toast({
        title: copy.successToastTitle,
        description: copy.successToastDescription,
        variant: 'default',
        className: 'bg-green-50 text-green-800 -translate-y-2',
      });
      dispatch(resetPolicyApplicationStateStatus());
    } else if (policyApplicationStatus === 'sendingFormEmailError') {
      toast({
        title: copy.errorToastTitle,
        description: copy.errorToastDescription,
        className: '-translate-y-2 bg-red-50 text-red-800 border border-red-200',
      });
      dispatch(resetPolicyApplicationStateStatus());
    }
  }, [
    policyApplicationStatus,
    copy.successToastTitle,
    copy.successToastDescription,
    copy.errorToastTitle,
    copy.errorToastDescription,
  ]);

  const handleSubmitApplication = () => {
    if (selectedSubmissionMethod === 'submit-to-bga-imo' && !associatedBGA) {
      return; // Don't submit if BGA is required but not configured
    }

    if (!selectedSubmissionMethod || !advisor || !client || !policyFundingSource) {
      return;
    }

    // Determine email address based on submission method
    let sentToEmail: string;
    if (selectedSubmissionMethod === 'submit-to-bga-imo') {
      if (!associatedBGA) {
        return;
      }
      sentToEmail = associatedBGA.contactEmail;
    } else if (selectedSubmissionMethod === 'submit-to-carrier') {
      sentToEmail = policyFormMetadata.carrierEmail;
    } else {
      return;
    }

    // Get form metadata for email - use the actual policyFormMetadata passed as prop
    const policyFormMetaData = {
      policyName: policyFormMetadata.product,
      formName: policyFormMetadata.title,
    };

    // Dispatch the email action
    dispatch(
      postPolicyApplicationEmailRequest({
        advisorId: advisor.advisorId,
        clientId: client.clientId,
        sentToEmail,
        policyFormMetaData,
        policyApplicationFormId: policyFormMetadata.formId,
        clientPolicyApplicationId,
        policyFundingSource,
      }),
    );
  };

  return (
    <div className="relative">
      {isSendingEmail && (
        <div className="absolute inset-0 z-50 grid size-full scale-[1.02] place-items-center bg-white/50 backdrop-blur-sm">
          <div className="text-gray-500">
            <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-gray-500" />
            Submitting...
          </div>
        </div>
      )}

      {/* Submission Method Selection */}
      <div className="mx-auto mb-8 max-w-3xl">
        <h3 className="mb-1 text-left text-lg font-semibold text-gray-900">
          Choose submission Method
        </h3>
        <p className="mb-6 text-left text-gray-600">
          Choose your next step to move forward with the application process
        </p>

        <RadioGroup
          value={selectedSubmissionMethod}
          onValueChange={value => onSubmissionMethodChange?.(value as SubmissionMethod)}
          className="space-y-3"
          disabled={!isAdvisor}
        >
          {/* Submit to Carrier Option */}
          <div className="relative">
            <Label
              htmlFor="submit-to-carrier"
              className={cn(
                "hover:border-primary-400 peer-checked:border-primary-600 peer-checked:ring-primary-600 hover:bg-primary-50/10 peer-checked:bg-primary-50 flex rounded-lg border border-gray-300 p-4 text-left transition-all peer-checked:ring-1",
                isAdvisor ? "cursor-pointer" : "cursor-not-allowed opacity-50"
              )}
            >
              <div className="flex items-start">
                <RadioGroupItem
                  value="submit-to-carrier"
                  id="submit-to-carrier"
                  className="peer mr-3 mt-0.5"
                  disabled={!isAdvisor}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">Submit to Carrier</span>
                    <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">
                      Direct Processing
                    </Badge>
                  </div>
                  <div className="mt-1 text-sm text-gray-500">
                    Submit directly to the insurance carrier for processing. This is typically
                    faster and provides direct communication with the underwriting team.
                  </div>
                </div>
              </div>
            </Label>
          </div>

          {/* Submit to BGA/IMO Option */}
          <div className="relative">
            <Label
              htmlFor="submit-to-bga-imo"
              className={cn(
                'hover:border-primary-400 peer-checked:border-primary-600 peer-checked:ring-primary-600 hover:bg-primary-50/10 peer-checked:bg-primary-50 flex rounded-lg border border-gray-300 p-4 text-left transition-all peer-checked:ring-1',
                selectedSubmissionMethod === 'submit-to-bga-imo' && ' hover:bg-white',
                isAdvisor ? "cursor-pointer" : "cursor-not-allowed opacity-50"
              )}
            >
              <div className="flex items-start">
                <RadioGroupItem
                  value="submit-to-bga-imo"
                  id="submit-to-bga-imo"
                  className="peer mr-3 mt-0.5"
                  disabled={!isAdvisor}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">Submit to BGA/IMO</span>
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-200">
                      Agent Review
                    </Badge>
                  </div>
                  <div className="mt-1 text-sm text-gray-500">
                    Submit through your Brokerage General Agent or Independent Marketing
                    Organization. They will review and forward to the appropriate carrier.
                  </div>

                  {/* BGA Configuration Status - Show when BGA/IMO is selected */}
                  {selectedSubmissionMethod === 'submit-to-bga-imo' && (
                    <div className="mt-4 space-y-3 rounded-md bg-gray-50 p-4">
                      {needsBGAConfiguration ? (
                        <div className="space-y-3">
                          <p className="text-sm text-gray-600">
                            No BGA/IMO configured for {getCarrierName()}. Configure one to continue.
                          </p>
                          <div className="flex justify-start">
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => {
                                const url = `/advisors/${advisor.advisorId}/settings?tab=carrier-bga`;
                                window.open(url, '_blank');
                                setShowRefreshMessage(true);
                              }}
                              className="bg-primary hover:bg-primary/90 h-9 px-4 text-white"
                            >
                              <HiOutlineExternalLink className="mr-2 h-4 w-4" />
                              Configure BGA for {getCarrierName()}
                            </Button>
                          </div>
                          {showRefreshMessage && (
                            <div className="flex items-center justify-between rounded border border-gray-200 bg-white px-3 py-2">
                              <p className="text-xs text-gray-600">
                                Refresh to see new configuration
                              </p>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.location.reload()}
                                className="h-6 px-2 text-xs text-gray-600 hover:text-gray-900"
                              >
                                <RefreshCw className="mr-1 h-3 w-3" />
                                Refresh
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : hasBGAForCarrier ? (
                        <div className="flex items-center space-x-2">
                          <div className="flex h-2 w-2 rounded-full bg-green-500"></div>
                          <p className="text-sm text-gray-600">
                            Will submit to:{' '}
                            <span className="font-medium">{associatedBGA?.name}</span> •{' '}
                            {associatedBGA?.contactEmail}
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Loading BGA configuration...</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Submit Button */}
      <StepActions>
        {formType === 'pre-underwriting' && (
          <StepSkipButton
            onClick={() => onSkipForm?.()}
            isLoading={isSkipping}
          >
            {copy.skipTitle}
          </StepSkipButton>
        )}
        {formType === 'pre-underwriting' ? (
          <ApplicationConsentDialog
            clientPolicyApplicationId={clientPolicyApplicationId}
            handleSubmitApplication={handleSubmitApplication}
          >
            <StepActionButton
              onClick={() => null}
              disabled={
                isSendingEmail ||
                !isAdvisor ||
                !selectedSubmissionMethod ||
                (selectedSubmissionMethod === 'submit-to-bga-imo' && !associatedBGA)
              }
              icon={
                isSendingEmail ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Send className="ml-1 h-5 w-5" />
                )
              }
            >
              {isSendingEmail ? copy.sendingText : copy.buttonText}
            </StepActionButton>
          </ApplicationConsentDialog>
        ) : (
          <StepActionButton
            onClick={handleSubmitApplication}
            disabled={
              isSendingEmail ||
              !isAdvisor ||
              !selectedSubmissionMethod ||
              (selectedSubmissionMethod === 'submit-to-bga-imo' && !hasBGAForCarrier)
            }
            icon={
              isSendingEmail ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="ml-1 h-5 w-5" />
              )
            }
          >
            {isSendingEmail ? copy.sendingText : copy.buttonText}
          </StepActionButton>
        )}
      </StepActions>
    </div>
  );
}
